/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { gql } from "graphql-tag";
import { useMutation } from "@apollo/client";
import { Grid, Typography, Link as MuiLink } from "@mui/material";
import { navigate } from "utils/navigation";
import { Viewer } from "components/auth/types";
import {
  TemplateGroupCreateInput,
  UserRole,
  createTemplateGroup,
  createTemplateGroupVariables,
} from "types/gql-types";
import logger from "services/logger";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { useSnackbar } from "components/haven-design/snackbar";
import { TEMPLATE_DASHBOARD_ROUTE } from "../template-groups";
import { TemplateForm } from "./form";

const CREATE_TEMPLATE = gql`
  mutation createTemplateGroup($data: TemplateGroupCreateInput!) {
    createTemplateGroup(data: $data) {
      id
    }
  }
`;

const SERVER_ERROR =
  "Unable to create template. Please try again or contact support@havenconnect.com.";

type Props = {
  viewer: Viewer;
}

const NewTemplate: React.FC<Props> = (props) => {
  const [createTemplateGroupFunc] = useMutation<
  createTemplateGroup,
  createTemplateGroupVariables
  >(CREATE_TEMPLATE, { refetchQueries: ["myTemplateGroups"] });
  const [isSending, setIsSending] = useState(false);

  const { openSnackbar } = useSnackbar();

  const handleSubmit = (templateData: TemplateGroupCreateInput) => {
    setIsSending(true);
    createTemplateGroupFunc({ variables: { data: templateData } })
      .then(({ data }) => {
        openSnackbar({ message: "Template created successfully", status: "success", duration: 3000 });
        navigate(`/dash/manager/settings/templates/update/${data?.createTemplateGroup?.id}`);
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: SERVER_ERROR, status: "error", duration: 5000 });
      })
      .finally(() => setIsSending(false));
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" component="div">
              <MuiLink
                component={Link}
                to={TEMPLATE_DASHBOARD_ROUTE}
                color="primary"
              >
                Templates
              </MuiLink>{" "}
              &gt; Create Template
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TemplateForm
              isSending={isSending}
              handleSubmit={handleSubmit}
              viewer={props.viewer}
              onCancel={() => navigate(TEMPLATE_DASHBOARD_ROUTE)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default authorizeForRole([UserRole.PROPERTY_MANAGER], NewTemplate);
